import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Save, X, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-navy px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";

const btnGold = "flex items-center gap-2 rounded-full bg-gold px-5 py-2 text-sm font-semibold text-navy hover:bg-gold-light disabled:opacity-60";
const btnGhost = "flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-slate-300 hover:border-gold/50";

function Toggle({ checked, onChange, testid }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="h-4 w-4 accent-gold" />
      {checked ? <span className="text-gold">Publié (visible)</span> : <span>Brouillon (masqué)</span>}
    </label>
  );
}

export function ArticlesManager({ passcode }) {
  const empty = { slug: "", tag: "Conseil", title: "", read: "3 min", excerpt: "", cover: "", content: "", published: false };
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null); // null | {form, id|null}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.adminArticles(passcode).then((r) => setRows(r.articles || [])).catch(() => {});
  }, [passcode]);
  useEffect(load, [load]);

  const save = async () => {
    setBusy(true);
    try {
      if (edit.id) await api.adminUpdateArticle(passcode, edit.id, edit.form);
      else await api.adminCreateArticle(passcode, edit.form);
      toast.success("Article enregistré.");
      setEdit(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'enregistrement."); }
    finally { setBusy(false); }
  };

  const del = async (a) => {
    if (!window.confirm(`Supprimer l'article « ${a.title} » ?`)) return;
    await api.adminDeleteArticle(passcode, a.id).catch(() => {});
    load();
  };

  if (edit) {
    const f = edit.form;
    const set = (k, v) => setEdit({ ...edit, form: { ...f, [k]: v } });
    return (
      <div className="max-w-3xl space-y-4 rounded-2xl border border-white/10 bg-navy-surface/60 p-6" data-testid="article-editor">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl text-white">{edit.id ? "Modifier l'article" : "Nouvel article"}</h3>
          <button onClick={() => setEdit(null)} className="text-slate-400 hover:text-gold" aria-label="Fermer"><X className="h-5 w-5" /></button>
        </div>
        <input placeholder="Titre de l'article" value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="article-title" />
        <div className="grid gap-3 sm:grid-cols-3">
          <input placeholder="Slug (auto si vide)" value={f.slug} onChange={(e) => set("slug", e.target.value)} className={inputCls} data-testid="article-slug" />
          <input placeholder="Étiquette (ex. Entretien)" value={f.tag} onChange={(e) => set("tag", e.target.value)} className={inputCls} data-testid="article-tag" />
          <input placeholder="Durée (ex. 4 min)" value={f.read} onChange={(e) => set("read", e.target.value)} className={inputCls} data-testid="article-read" />
        </div>
        <input placeholder="Résumé (affiché sur la carte)" value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} className={inputCls} data-testid="article-excerpt" />
        <input placeholder="Image de couverture — URL (optionnel)" value={f.cover} onChange={(e) => set("cover", e.target.value)} className={inputCls} data-testid="article-cover" />
        <textarea rows={10} placeholder={"Contenu de l'article — une ligne = un paragraphe"} value={f.content} onChange={(e) => set("content", e.target.value)} className={inputCls} data-testid="article-content" />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Toggle checked={f.published} onChange={(v) => set("published", v)} testid="article-published" />
          <button onClick={save} disabled={busy || f.title.trim().length < 3} className={btnGold} data-testid="article-save">
            <Save className="h-4 w-4" /> Enregistrer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="articles-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">Les articles publiés apparaissent sur la page Conseils.</p>
        <button onClick={() => setEdit({ form: empty, id: null })} className={btnGold} data-testid="article-new">
          <Plus className="h-4 w-4" /> Nouvel article
        </button>
      </div>
      {rows.map((a) => (
        <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-navy-surface/60 p-4" data-testid={`article-row-${a.slug}`}>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{a.title}</p>
            <p className="text-xs text-slate-500">/conseils/{a.slug} · {a.tag}</p>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs ${a.published ? "bg-gold/15 text-gold" : "border border-white/15 text-slate-400"}`}>
            {a.published ? "Publié" : "Brouillon"}
          </span>
          {a.published && (
            <a href={`/conseils/${a.slug}`} target="_blank" rel="noopener noreferrer" className={btnGhost} data-testid={`article-view-${a.slug}`}>
              <Eye className="h-3.5 w-3.5" /> Voir
            </a>
          )}
          <button onClick={() => setEdit({ form: { ...empty, ...a }, id: a.id })} className={btnGhost} data-testid={`article-edit-${a.slug}`}>
            <Pencil className="h-3.5 w-3.5" /> Modifier
          </button>
          <button onClick={() => del(a)} className="rounded-full border border-white/15 p-2 text-slate-400 hover:border-red-400/50 hover:text-red-300" aria-label="Supprimer" data-testid={`article-del-${a.slug}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      {rows.length === 0 && <p className="text-sm text-slate-500">Aucun article.</p>}
    </div>
  );
}

export function PagesManager({ passcode }) {
  const empty = { slug: "", title: "", image: "", body: "", published: false };
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.adminPages(passcode).then((r) => setRows(r.pages || [])).catch(() => {});
  }, [passcode]);
  useEffect(load, [load]);

  const save = async () => {
    setBusy(true);
    try {
      if (edit.id) await api.adminUpdatePage(passcode, edit.id, edit.form);
      else await api.adminCreatePage(passcode, edit.form);
      toast.success("Page enregistrée.");
      setEdit(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'enregistrement."); }
    finally { setBusy(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Supprimer la page « ${p.title} » ?`)) return;
    await api.adminDeletePage(passcode, p.id).catch(() => {});
    load();
  };

  if (edit) {
    const f = edit.form;
    const set = (k, v) => setEdit({ ...edit, form: { ...f, [k]: v } });
    return (
      <div className="max-w-3xl space-y-4 rounded-2xl border border-white/10 bg-navy-surface/60 p-6" data-testid="page-editor">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl text-white">{edit.id ? "Modifier la page" : "Nouvelle page"}</h3>
          <button onClick={() => setEdit(null)} className="text-slate-400 hover:text-gold" aria-label="Fermer"><X className="h-5 w-5" /></button>
        </div>
        <input placeholder="Titre de la page" value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="page-title" />
        <input placeholder="Slug (auto si vide) — adresse : /p/votre-slug" value={f.slug} onChange={(e) => set("slug", e.target.value)} className={inputCls} data-testid="page-slug" />
        <input placeholder="Image d'en-tête — URL (optionnel)" value={f.image} onChange={(e) => set("image", e.target.value)} className={inputCls} data-testid="page-image" />
        <textarea rows={12} placeholder={"Texte de la page — une ligne = un paragraphe"} value={f.body} onChange={(e) => set("body", e.target.value)} className={inputCls} data-testid="page-body" />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Toggle checked={f.published} onChange={(v) => set("published", v)} testid="page-published" />
          <button onClick={save} disabled={busy || f.title.trim().length < 3} className={btnGold} data-testid="page-save">
            <Save className="h-4 w-4" /> Enregistrer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pages-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400">Les pages publiées apparaissent dans le pied de page et sont accessibles via /p/…</p>
        <button onClick={() => setEdit({ form: empty, id: null })} className={btnGold} data-testid="page-new">
          <Plus className="h-4 w-4" /> Nouvelle page
        </button>
      </div>
      {rows.map((p) => (
        <div key={p.id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-navy-surface/60 p-4" data-testid={`page-row-${p.slug}`}>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{p.title}</p>
            <p className="text-xs text-slate-500">/p/{p.slug}</p>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs ${p.published ? "bg-gold/15 text-gold" : "border border-white/15 text-slate-400"}`}>
            {p.published ? "Publiée" : "Brouillon"}
          </span>
          {p.published && (
            <a href={`/p/${p.slug}`} target="_blank" rel="noopener noreferrer" className={btnGhost} data-testid={`page-view-${p.slug}`}>
              <Eye className="h-3.5 w-3.5" /> Voir
            </a>
          )}
          <button onClick={() => setEdit({ form: { ...empty, ...p }, id: p.id })} className={btnGhost} data-testid={`page-edit-${p.slug}`}>
            <Pencil className="h-3.5 w-3.5" /> Modifier
          </button>
          <button onClick={() => del(p)} className="rounded-full border border-white/15 p-2 text-slate-400 hover:border-red-400/50 hover:text-red-300" aria-label="Supprimer" data-testid={`page-del-${p.slug}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      {rows.length === 0 && <p className="text-sm text-slate-500">Aucune page libre pour le moment.</p>}
    </div>
  );
}
